/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

/**
 * @typedef {Object} MutationObserverActionOptions
 * @property {Function} [callback=() => {}] - see https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/MutationObserver#callback
 * @property {Object} [options={ attributes: true }] - see https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/observe#options
 */

/**
 *
 * @param {Element} node
 * @param {MutationObserverActionOptions} options
 * @returns {Object}
 */
export default function useMutationObserver(node, options) {
    let mutationObserver;
    let nodeCallback;

    const destroy = () => {
        if (!mutationObserver) {
            return;
        }
        const pendingMutations = mutationObserver.takeRecords();
        mutationObserver.disconnect();
        if (pendingMutations.length) {
            nodeCallback(pendingMutations);
        }
        mutationObserver = null;
    };

    const update = (newOptions = {}) => {
        destroy();
        const { callback = () => { }, observeOptions = { attributes: true } } = newOptions;
        nodeCallback = callback;
        mutationObserver = new MutationObserver(nodeCallback);
        mutationObserver.observe(node, observeOptions);
    };

    update(options);

    return {
        update,
        destroy
    };
}